import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Redirect, Route, useHistory, useLocation } from 'react-router-dom';
import { failRoutes, loadingRoutes, successRoutes } from 'routes';
import { Auth } from 'models';
import Subheader from './components/Subheader';
import { integrationOptions } from 'resources/IntegrationsData';
import LayoutView from './LayoutView';
import { getRawPropertyData } from 'models/Cropwise';
import { RoutePaths } from 'routes/history';
import { useTranslation } from 'react-i18next';
import { useDeepActionOrgId } from './hooks/useDeepActionOrgId';

import { useResourceMapperStore } from '../ResourceMapper/store/useResourceMapperStore';
import { useFieldMatchingStore } from '../FieldMatching/store/field-matching.store';
import { APIState } from '../../models/APIState';
import { replaceQueryParam } from '../../utils/replaceQueryParam';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  evaluateBooleanFeatureFlag,
  LDFeatureFlagEnum,
} from 'tools/feature-flags/launchdarkly-flags';
import DemoMFEProvider from 'modules/demo-mfe/views/DemoMFEProvider';
import { Organization } from 'models/AuthModel';
import { setAmplitudeOrganizationDetails } from 'services/amplitudeHelper';

const LayoutController = observer(({ viewModel }: { viewModel: Auth }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedOrgName, setSelectedOrgName] = useState<string>('');
  const [farms, setFarms] = useState<any[]>([]);
  const [selectedFarmName, setSelectedFarmName] = useState<string>('');
  const firstPass = useRef(true);
  const [integrationName, setIntegrationName] = useState<string>('');

  const location = useLocation();
  const history = useHistory();
  const { t } = useTranslation();
  const translatedAllFarms = t('All Farms');
  const { setOrgId } = useFieldMatchingStore();
  const { unmappedResourcesStatus } = useResourceMapperStore();
  const flags = useFlags();
  const isEnabledMfeDemoPage = evaluateBooleanFeatureFlag(
    flags,
    LDFeatureFlagEnum.SHOW_MFE_DEMO_PAGE,
  );

  useEffect(() => {
    if (location && location.pathname.indexOf('integrations') !== -1) {
      const locationItems = location.pathname.split('/');
      if (locationItems.length >= 2) {
        const integrationFilter = integrationOptions.filter((integration) => {
          return integration.partner_id === locationItems[2];
        });
        if (integrationFilter && integrationFilter.length > 0) {
          setIntegrationName(integrationFilter[0].integration_name);
        }
      }
    }
  }, [location]);

  const { deepActionOrgId } = useDeepActionOrgId();

  useEffect(() => {
    const getOrgs = async () => {
      firstPass.current = false;
      const orgs = await viewModel.organizations(deepActionOrgId);
      let orgId = '';
      if (orgs?.length) {
        orgId = viewModel?.activeOrganization?.id ?? orgs[0].id;
        const org = orgs.find((o) => o.id === orgId) || orgs[0];
        setAmplitudeOrganizationDetails(org);
        setSelectedOrgId(orgId);
        setSelectedOrgName(viewModel?.activeOrganization?.name ?? orgs[0].name);
      }
      setOrganizations(orgs);
    };
    if (viewModel.userId && firstPass.current) {
      getOrgs().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewModel,
    viewModel.organizations,
    viewModel.userId,
    organizations.length,
    selectedOrgId,
    selectedOrgName,
    deepActionOrgId,
  ]);

  useEffect(() => {
    if (selectedOrgId) setOrgId(selectedOrgId);
  }, [selectedOrgId, setOrgId]);

  // These two functions can be deleted once we adopt the new Top Nav Bar
  const buildOrgClickHandler = useCallback(
    (org: Organization) => {
      replaceQueryParam('org_id', org.id, history);
      setSelectedOrgId(org.id);
      setSelectedOrgName(org.name);
      setSelectedFarmName(translatedAllFarms);
      viewModel.setSelectedFarmId('0');
      runInAction(() => {
        viewModel.activeOrganization = org;
      });
    },
    [translatedAllFarms, viewModel, history],
  );

  const mapOrgsToOrgMenuItems = useCallback(
    (orgs: any[]) =>
      orgs.map((org) => ({
        name: org.name,
        id: org.id,
        key: org.id,
        icon: () => <img src={org.picture_url} alt={org.name} />,
        onClick: () => buildOrgClickHandler(org),
      })),
    [buildOrgClickHandler],
  );

  useEffect(() => {
    setFarms([]);
    const getFarms = async () => {
      let _farms = await getRawPropertyData(selectedOrgId);
      if (!_farms) _farms = [];
      _farms.sort(({ name: name1 }: any, { name: name2 }: any) => (name1 > name2 ? 1 : -1));
      setSelectedFarmName(translatedAllFarms);
      viewModel.setSelectedFarmId('0');

      if (_farms.length && _farms[0].id) {
        viewModel.setFirstFarmId(_farms[0].id);
      }

      let farmMenuItems = _farms.map((farm: any) => ({
        name: farm.name,
        id: farm.id,
        key: farm.id,
        onClick: () => {
          setSelectedFarmName(farm.name);
          viewModel.setSelectedFarmId(farm.id);
        },
      }));
      farmMenuItems = [
        {
          name: translatedAllFarms,
          id: '0',
          key: '0',
          onClick: () => {
            setSelectedFarmName(translatedAllFarms);
            viewModel.setSelectedFarmId('0');
          },
        },
        ...farmMenuItems,
      ];

      setFarms(farmMenuItems);
    };

    if (selectedOrgId) {
      getFarms().catch(console.error);
    }
  }, [viewModel.token.access_token, translatedAllFarms, selectedOrgId, viewModel]);

  const determineRoutes = useCallback(() => {
    const returnArray = [];
    if (viewModel.status === APIState.SUCCESS) {
      successRoutes.forEach(({ path, manual_subheader, Component }, key) => {
        returnArray.push(
          <Route
            key={key}
            path={path}
            render={(props) => {
              const crumbs = successRoutes.filter(({ path: _path }) =>
                props.match.path.includes(_path),
              );
              const renderSubHeader =
                !manual_subheader && !crumbs.map((c) => c.name).includes('Timeline');
              return (
                <div className={`h-100% ${renderSubHeader ? 'p-8' : ''}`}>
                  {renderSubHeader && (
                    <Subheader crumbs={crumbs} integrationName={integrationName} />
                  )}
                  <Component />
                </div>
              );
            }}
          />,
        );
      });
      if (isEnabledMfeDemoPage) {
        returnArray.push(
          <Route
            key={RoutePaths.DemoMFE()}
            path={RoutePaths.DemoMFE()}
            render={() => {
              return (
                <div className={`h-100%`}>
                  <DemoMFEProvider />
                </div>
              );
            }}
          />,
        );
      }
      returnArray.push(<Redirect key={RoutePaths.DefaultRoute()} to={RoutePaths.DefaultRoute()} />);
    } else if (viewModel.status === APIState.LOADING) {
      loadingRoutes.forEach(({ path, component }) => {
        returnArray.push(<Route key={path} path={path} component={component} />);
      });
    } else if (viewModel.status === APIState.FAIL) {
      failRoutes.forEach(({ path, component }) => {
        returnArray.push(<Route exact key={path} path={path} component={component} />);
      });
      returnArray.push(<Redirect key="/" to="/" />);
    }
    return returnArray;
  }, [integrationName, viewModel, isEnabledMfeDemoPage]);

  const selectOrg = useCallback(
    (orgId: string) => {
      const org = organizations.find((o) => o.id === orgId);
      if (!org) return;
      replaceQueryParam('org_id', org.id, history);
      setSelectedOrgId(org.id);
      setSelectedOrgName(org.name);
      setAmplitudeOrganizationDetails(org);

      runInAction(() => {
        viewModel.activeOrganization = org;
      });
    },
    [organizations, history, viewModel.activeOrganization],
  );

  return (
    <LayoutView
      viewModel={viewModel}
      unmappedResourcesStatus={unmappedResourcesStatus}
      routes={determineRoutes}
      organizations={mapOrgsToOrgMenuItems(organizations)}
      selectedOrgId={selectedOrgId}
      selectedOrgName={selectedOrgName}
      farms={farms}
      selectedFarmName={selectedFarmName}
      setSelectedOrg={selectOrg}
    />
  );
});

export default LayoutController;
